import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AIPanel from "../../../components/AIChat/AIPanel";
import { Loading } from "spark-core-dx/components";
import { useCoreStateContext } from "spark-core-dx/contexts";
import { useCoreRoute } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import PermissionSetEditor from "../../../components/PermissionSetEditor";
import { useGenericManager } from "spark-core-dx/hooks";
import { AI_PANEL_GRID_REFRESH_EVENT } from "../../../components/AIChat/aiPanelCommand";

const firstItem = (items) => items?.first?.() ?? (Array.isArray(items) ? items[0] : items) ?? null;

const getPanelParam = (params, ...keys) => {
  if (!params || typeof params !== "object") return "";
  for (const key of keys) {
    const value = params[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || !["{", "["].includes(trimmed[0])) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const getObjectPropCI = (obj, ...names) => {
  if (!obj || typeof obj !== "object") return undefined;
  const lookup = new Map(Object.keys(obj).map((key) => [key.toLowerCase(), key]));
  for (const name of names) {
    const key = lookup.get(String(name).toLowerCase());
    if (key !== undefined) return obj[key];
  }
  return undefined;
};

const looksLikePermissionEntry = (entry) =>
  Boolean(
    entry &&
      typeof entry === "object" &&
      String(getObjectPropCI(entry, "Permission", "permission", "PermissionId", "permissionId") ?? "").trim()
  );

const extractPermissionEntries = (value, depth = 0, seen = new Set()) => {
  const parsed = parseMaybeJson(value);
  if (depth > 8 || parsed == null) return null;

  if (Array.isArray(parsed)) {
    const entries = parsed.filter(looksLikePermissionEntry);
    return entries.length ? entries : null;
  }

  if (typeof parsed !== "object") return null;
  if (seen.has(parsed)) return null;
  seen.add(parsed);

  const directEntries = getObjectPropCI(parsed, "Entries", "entries");
  if (Array.isArray(directEntries)) return directEntries;

  for (const key of ["permissionSets", "PermissionSets", "permissions", "Permissions"]) {
    const entries = extractPermissionEntries(getObjectPropCI(parsed, key), depth + 1, seen);
    if (entries) return entries;
  }

  for (const key of ["payload", "Payload", "output", "Output", "data", "Data", "value", "Value", "permissionSet", "PermissionSet"]) {
    const entries = extractPermissionEntries(getObjectPropCI(parsed, key), depth + 1, seen);
    if (entries) return entries;
  }

  return null;
};

const resolvePermissionSetUpdate = (item, value, basePermissionSet) => {
  const candidates = [item, value].map(parseMaybeJson);
  for (const candidate of candidates) {
    const entries = extractPermissionEntries(candidate);
    if (!entries) continue;
    const objectCandidate = candidate && typeof candidate === "object" && !Array.isArray(candidate) ? candidate : {};
    return {
      ...(basePermissionSet ?? {}),
      ...objectCandidate,
      _id: objectCandidate?._id ?? basePermissionSet?._id,
      Entries: entries
    };
  }
  return null;
};

const markAcceptedAIDraft = (permissionSetDraft) => ({
  ...(permissionSetDraft ?? {}),
  __aiAcceptedDraft: true,
  __refreshKey: Date.now()
});

const PANEL_TRANSITION_MS = 260;
const PERMISSION_SET_PANEL_MIN_WIDTH = 1225;

const PermissionSetManage = ({
  panelMode = false,
  panelOpen = true,
  panelId = "",
  panelSessionId = "",
  panelParams = null,
  onPanelClose = null
}) => {
  const itemService = useMemo(() => new ItemServiceFactory(), []);
  const { coreNavigate } = useCoreRoute();
  const coreState = useCoreStateContext();
  const pageRef = useRef(null);
  const coreStateRef = useRef(coreState);

  const permissionSetMgr = useGenericManager(itemService.ItemServiceEnum.PermissionSet);
  const permissionMgr = useGenericManager(itemService.ItemServiceEnum.Permission);
  const chatSessionMgr = useGenericManager(itemService.ItemServiceEnum.AIChatSession);
  const managersRef = useRef({});

  const [permissions, setPermissions] = useState([]);
  const [permissionSet, setPermissionSet] = useState(null);
  const [currentPermissionSet, setCurrentPermissionSet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState("0");
  const [session, setSession] = useState(null);
  const [panelContentReady, setPanelContentReady] = useState(!panelMode);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();

  const panelParamId = useMemo(
    () => getPanelParam(panelParams, "permissionSetId", "PermissionSetId", "id", "_id"),
    [panelParams]
  );
  const searchParamsText = searchParams.toString();
  const searchParamIds = useMemo(() => {
    const params = new URLSearchParams(searchParamsText);
    return {
      permissionSetId: String(params.get("permissionSetId") ?? "").trim(),
      id: String(params.get("id") ?? "").trim()
    };
  }, [searchParamsText]);

  const effectiveId = useMemo(() => {
    return (
      String(panelId ?? "").trim() ||
      panelParamId ||
      searchParamIds.permissionSetId ||
      searchParamIds.id ||
      String(routeId ?? "").trim()
    );
  }, [panelId, panelParamId, routeId, searchParamIds.id, searchParamIds.permissionSetId]);

  const lastPayloadStrRef = useRef("");
  const loadKeyRef = useRef("");
  const loadInFlightKeyRef = useRef("");
  const canLoadData = !panelMode || panelContentReady;

  useEffect(() => {
    managersRef.current = {
      permissionSetMgr,
      permissionMgr,
      chatSessionMgr
    };
  }, [chatSessionMgr, permissionMgr, permissionSetMgr]);

  useEffect(() => {
    coreStateRef.current = coreState;
  }, [coreState]);

  const setSessionIfChanged = useCallback((nextSession) => {
    setSession((prev) => {
      if (!prev && !nextSession) return prev;
      if (prev === nextSession) return prev;
      try {
        if (JSON.stringify(prev ?? null) === JSON.stringify(nextSession ?? null)) {
          return prev;
        }
      } catch {
        // fall back to object identity above
      }
      return nextSession;
    });
  }, []);

  const setCurrentPermissionSetIfChanged = useCallback((next) => {
    let nextStr = "";
    try {
      nextStr = JSON.stringify(next);
    } catch {
      // fall back to reference comparison below
    }

    if (nextStr && nextStr === lastPayloadStrRef.current) return;

    setCurrentPermissionSet((prev) => {
      try {
        const prevStr = JSON.stringify(prev);
        if (prevStr === nextStr) return prev;
      } catch {
        if (prev === next) return prev;
      }
      lastPayloadStrRef.current = nextStr || "";
      return next;
    });
  }, []);

  const getChatSession = useCallback(async (referenceId) => {
    if (!referenceId) return null;
    if (panelSessionId) {
      return {
        _id: panelSessionId,
        ReferenceId: referenceId,
        ReferenceItemName: "PermissionSet"
      };
    }

    const chatSessionResult = await managersRef.current.chatSessionMgr.Query({
      model: "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
      params: {
        referenceId,
        referenceItemName: "PermissionSet"
      }
    });

    if (!chatSessionResult?.Success || !Array.isArray(chatSessionResult?.Items) || chatSessionResult.Items.length === 0) {
      return null;
    }

    return [...chatSessionResult.Items].sort((left, right) => {
      const rightDate = Date.parse(right?.ModifiedDate ?? right?.CreatedDate ?? "") || Number(right?.Sequence ?? 0);
      const leftDate = Date.parse(left?.ModifiedDate ?? left?.CreatedDate ?? "") || Number(left?.Sequence ?? 0);
      return rightDate - leftDate;
    })[0];
  }, [panelSessionId]);

  const dispatchGridRefresh = useCallback(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(AI_PANEL_GRID_REFRESH_EVENT, {
      cancelable: true,
      detail: { itemName: "PermissionSet" }
    }));
  }, []);

  useEffect(() => {
    if (!panelMode) {
      setPanelContentReady(true);
      return undefined;
    }

    if (!panelOpen) {
      setPanelContentReady(false);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setPanelContentReady(true);
    }, PANEL_TRANSITION_MS);

    return () => window.clearTimeout(timerId);
  }, [panelMode, panelOpen]);

  useEffect(() => {
    let cancelled = false;
    let startedLoad = false;
    const loadKey = `${panelMode ? "panel" : "route"}|${effectiveId}|${panelSessionId || ""}`;

    const getPermissionData = async () => {
      if (!canLoadData) {
        return;
      }

      if (!effectiveId) {
        loadKeyRef.current = "";
        loadInFlightKeyRef.current = "";
        if (!cancelled) {
          setIsLoading(false);
          setPermissions([]);
          setPermissionSet(null);
          setCurrentPermissionSetIfChanged(null);
          setSessionIfChanged(null);
        }
        return;
      }

      if (loadKeyRef.current === loadKey || loadInFlightKeyRef.current === loadKey) {
        return;
      }

      startedLoad = true;
      loadInFlightKeyRef.current = loadKey;
      setIsLoading(true);

      const { permissionMgr: currentPermissionMgr, permissionSetMgr: currentPermissionSetMgr } = managersRef.current;
      const permissionResult = await currentPermissionMgr.Query("x => x.IsDeleted == false && x.IsActive == true");
      const nextPermissions = permissionResult?.Success ? permissionResult.Items ?? [] : [];

      let nextPermissionSet = null;
      const redirectedPermissionSet = panelMode ? null : coreStateRef.current?.get?.("InternalRedirectData");
      if (redirectedPermissionSet && String(redirectedPermissionSet?._id ?? "") === effectiveId) {
        nextPermissionSet = redirectedPermissionSet;
      } else {
        const permissionSetResult = await currentPermissionSetMgr.getItem(effectiveId);
        if (permissionSetResult?.Success) {
          nextPermissionSet = firstItem(permissionSetResult.Items);
        }
      }

      const nextSession = await getChatSession(effectiveId);

      if (cancelled) return;
      setPermissions(nextPermissions);
      setPermissionSet(nextPermissionSet);
      setCurrentPermissionSetIfChanged(nextPermissionSet);
      setSessionIfChanged(nextSession);
      loadKeyRef.current = loadKey;
      setIsLoading(false);
    };

    getPermissionData().catch(() => {
      if (!cancelled) {
        loadKeyRef.current = loadKey;
        setIsLoading(false);
      }
    }).finally(() => {
      if (startedLoad && loadInFlightKeyRef.current === loadKey) {
        loadInFlightKeyRef.current = "";
      }
    });

    return () => {
      cancelled = true;
    };
  }, [canLoadData, effectiveId, getChatSession, panelMode, panelSessionId, setCurrentPermissionSetIfChanged, setSessionIfChanged]);

  const onUpdatePanel = useCallback(async (UICommand, item, value) => {
    if (String(UICommand ?? "").toLowerCase() === "update" && item?.Entries) {
      setCurrentPermissionSetIfChanged(item);
      setRefresh(markAcceptedAIDraft(item));
      return;
    }

    if (String(UICommand ?? "").toLowerCase() === "update") {
      const updatedPermissionSet = resolvePermissionSetUpdate(item, value, currentPermissionSet ?? permissionSet);
      if (updatedPermissionSet?.Entries) {
        setCurrentPermissionSetIfChanged(updatedPermissionSet);
        setRefresh(markAcceptedAIDraft(updatedPermissionSet));
      }
    }
  }, [currentPermissionSet, permissionSet, setCurrentPermissionSetIfChanged]);

  const handleSavePermissionSet = async (updatedPermissionSet) => {
    const result = await permissionSetMgr.saveItem(updatedPermissionSet);
    if (result?.Success) {
      setRefresh(String(Date.now()));
      const saved = firstItem(result.Items);
      setPermissionSet(saved);
      setCurrentPermissionSetIfChanged(saved);
      dispatchGridRefresh();
    }
    return result;
  };

  const handleCurrentPermissionSet = useCallback((value) => {
    setCurrentPermissionSetIfChanged(value);
  }, [setCurrentPermissionSetIfChanged]);

  const handleSessionChange = useCallback((sessionId, sessionItem) => {
    if (sessionItem) {
      setSessionIfChanged(sessionItem);
      return;
    }
    if (sessionId) {
      setSessionIfChanged({
        _id: sessionId,
        ReferenceId: effectiveId,
        ReferenceItemName: "PermissionSet"
      });
    }
  }, [effectiveId, setSessionIfChanged]);

  const handleCancel = useCallback(() => {
    if (panelMode) {
      if (typeof onPanelClose === "function") onPanelClose();
      return;
    }
    coreNavigate("/dynamic/permissionset/");
  }, [coreNavigate, onPanelClose, panelMode]);

  const handlePanelAnimationEnd = useCallback(() => {
    if (panelMode && panelOpen) {
      setPanelContentReady(true);
    }
  }, [panelMode, panelOpen]);

  const renderPanelContent = () => {
    if (isLoading) {
      return (
        <div className="centered-loading" ref={pageRef}>
          <Loading />
        </div>
      );
    }

    return (
      <div className="permission-set-editor" ref={pageRef}>
        <PermissionSetEditor
          permissionSet={permissionSet}
          permissions={permissions}
          onSave={handleSavePermissionSet}
          onChange={handleCurrentPermissionSet}
          refresh={refresh}
        />
      </div>
    );
  };

  if (!panelMode && isLoading) {
    return (
      <div className="centered-loading">
        <Loading />
      </div>
    );
  }

  return (
    <AIPanel
      childrenRef={pageRef}
      open={panelMode ? panelOpen : true}
      presentation={panelMode ? "slideout" : "inline"}
      title="Manage Permission Set"
      className={panelMode ? "ai-panel-floating-slideout" : ""}
      onUpdatePanel={onUpdatePanel}
      onSessionChange={handleSessionChange}
      onAnimationEnd={handlePanelAnimationEnd}
      mapToClass="core-layout"
      width={panelMode ? PERMISSION_SET_PANEL_MIN_WIDTH : undefined}
      minPanelWidth={panelMode ? PERMISSION_SET_PANEL_MIN_WIDTH : undefined}
      panelStorageKey={panelMode ? null : undefined}
      contentReady={panelMode ? panelContentReady : true}
      params={{ id: effectiveId, permissionSetId: effectiveId }}
      session={session}
      payload={currentPermissionSet}
      referenceId={effectiveId}
      enableContentDragHiding={false}
      minLeft={825}
      minRight={400}
      defaultOrchestrationMode="AgentAssist"
      singleSessionMode={true}
      chatPaneToggleEnabled={true}
      chatPaneCollapseMode="open"
      planningPreviewEnabled={true}
      previewPanelEnabled={true}
      previewRefreshEnabled={false}
      reviewContext={{ permissions }}
      referenceName="PermissionSet"
      operationName="AIPermissionSet"
      introMessage="Manage Permission Set..."
      splashMessage="How can I help you manage this permission set?"
      onCancel={handleCancel}
    >
      {renderPanelContent()}
    </AIPanel>
  );
};

export default PermissionSetManage;
